import axios from 'axios'

import { xhrRequestConfig } from '@/js/utils/api'
import { storefrontFetch } from '@/js/utils/storefront'
import { productQuery, transformGraphqlProduct } from '@/js/utils/data'

import { onMounted, ref, watch } from 'vue'

export default {
  props: {
    configs: {
      type: Object,
      required: false
    }
  },

  setup (props) {
    // VARIABLES
    const config = props.configs || {}

    const {
      freeGiftVariantId,
      threshold,
      freeGiftQuantityMode,
      trigger,
      freeGiftCollections = [],
      enableFreeGiftByQuantity,
      quantityThreshold,
      freeGiftQuantityCollections = []
    } = config || {}

    const collectionProductsCache = {}

    const showGiftProduct = ref(false)
    // Watch showGiftProduct changes
    watch(showGiftProduct, (newVal, oldVal) => {
      const giftDiv = document.getElementById('free-gift-container')
      if (giftDiv) {
        if (newVal) {
          giftDiv.style.display = 'flex'
        } else {
          giftDiv.style.display = 'none'
        }
      }
    })

    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/'

    // FUNCTIONS
    async function getCollectionHandles (handle) {
      if (!handle) return new Set()
      if (collectionProductsCache[handle]) return collectionProductsCache[handle]

      try {
        const query = `
                query getCollectionProducts($handle: String!, $cursor: String) {
                    collection(handle: $handle) {
                    products(first: 250, after: $cursor) {
                        edges {
                        cursor
                        node {
                            ${productQuery}
                        }
                        }
                        pageInfo {
                        hasNextPage
                        endCursor
                        }
                    }
                    }
                }
                `

        const handlesSet = new Set()
        let cursor = null
        let hasNextPage = true

        while (hasNextPage) {
          const data = await storefrontFetch(query, {
            handle,
            cursor
          })
          const collection = data && data.collection
          const productsConnection = collection && collection.products
          const edges = productsConnection ? productsConnection.edges : []

          edges.forEach(({
            node
          }) => {
            const product = transformGraphqlProduct(node, handle)
            if (product && product.handle) {
              handlesSet.add(product.handle)
            }
          })

          hasNextPage = productsConnection && productsConnection.pageInfo.hasNextPage
          cursor = hasNextPage ? productsConnection.pageInfo.endCursor : null
        }

        collectionProductsCache[handle] = handlesSet
      } catch (e) {
        collectionProductsCache[handle] = new Set()
      }

      return collectionProductsCache[handle]
    }

    async function addCart ({
      variantId,
      quantity,
      properties
    }) {
      const payload = {
        id: variantId,
        quantity
      }
      if (properties != null) {
        payload.properties = properties
      }
      await axios.post(`${root}cart/add.js`, payload, xhrRequestConfig)

      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        setTimeout(() => {
          window.ajaxCart.load()
        }, 100)
      }
    }

    async function changeCart ({
      key,
      quantity
    }) {
      await axios.post(`${root}cart/change.js`, {
        id: key,
        quantity
      }, xhrRequestConfig)

      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        setTimeout(() => {
          window.ajaxCart.load()
        }, 100)
      }
    }

    // LIFECYCLE
    onMounted(async () => {
      if (!freeGiftVariantId) return

      // Use full cart JSON so we have handles, gift_card flags and prices
      let fullCart
      try {
        const {
          data
        } = await axios.get(`${root}cart.js`)
        fullCart = data
      } catch (e) {
        console.log('Error loading cart.js for free gift logic', e)
        return
      }

      const cartItems = fullCart.items || []
      const cartTotal = fullCart.total_price

      if (!cartItems.length) return

      // If free gift feature is disabled or not configured, remove any zero-priced gift card items
      const zeroPriceGiftCards = cartItems.filter(item => item.id === freeGiftVariantId && item.final_price === 0)
      if (zeroPriceGiftCards.length && cartItems.length === 1) {
        for (const item of zeroPriceGiftCards) {
          try {
            await changeCart({
              key: item.key,
              quantity: 0
            })
            showGiftProduct.value = false
          } catch (e) {
            console.log('Error removing disabled free gift card item', item, e)
          }
        }
      }

      // Determine if all items are gift cards
      const allGiftCards = cartItems.every(item => item.gift_card)

      let shouldAddFreeGift = false
      let freeGiftQuantity = 0

      // Preload union of product handles in free gift collections
      const freeGiftCollectionHandlesSet = new Set()
      if (freeGiftCollections && freeGiftCollections.length) {
        for (const collectionHandle of freeGiftCollections) {
          const handlesSet = await getCollectionHandles(collectionHandle)
          handlesSet.forEach(h => freeGiftCollectionHandlesSet.add(h))
        }
      }
      // First rule: enable_free_gift with collection filtering and threshold
      if (freeGiftCollections && freeGiftCollections.length && cartTotal >= threshold) {
        let freeGiftCollectionsMatch = true
        if (trigger === 'include') freeGiftCollectionsMatch = false

        // Exclude the free gift item itself when evaluating collections
        const nonGiftItems = cartItems.filter(item => item.id !== freeGiftVariantId)

        if (freeGiftCollectionHandlesSet.size) {
          for (const item of nonGiftItems) {
            const handle = item.handle
            if (!handle) continue

            if (trigger === 'exclude' && freeGiftCollectionHandlesSet.has(handle)) {
              freeGiftCollectionsMatch = false
              break
            }

            if (trigger === 'include' && freeGiftCollectionHandlesSet.has(handle)) {
              freeGiftCollectionsMatch = true
              break
            }
          }
        }

        // Count cart items with quantities in free gift collections
        let collectionItemCount = 0
        if (freeGiftCollectionHandlesSet.size) {
          for (const item of nonGiftItems) {
            const handle = item.handle
            if (!handle) continue
            if (freeGiftCollectionHandlesSet.has(handle)) {
              collectionItemCount += item.quantity
            }
          }
        }

        if (freeGiftCollectionsMatch) {
          shouldAddFreeGift = true

          if (freeGiftQuantityMode === 'sku') {
            freeGiftQuantity = collectionItemCount
          } else {
            freeGiftQuantity = 1
          }
        }
      } else if (cartTotal >= threshold) {
        // If no collections configured, fall back to simple threshold rule
        shouldAddFreeGift = true
        freeGiftQuantity = 1
      }

      // Second rule: enable_free_gift_by_quantity
      if (enableFreeGiftByQuantity && freeGiftQuantityCollections && freeGiftQuantityCollections.length && quantityThreshold) {
        let freeGiftByQuantity = false

        for (const collectionHandle of freeGiftQuantityCollections) {
          const handlesSet = await getCollectionHandles(collectionHandle)
          if (!handlesSet.size) continue

          let countQuantityThreshold = 0

          for (const item of cartItems) {
            const handle = item.handle
            if (!handle) continue
            if (handlesSet.has(handle) && item.gift_card === false) {
              countQuantityThreshold += item.quantity
            }
          }

          if (countQuantityThreshold >= quantityThreshold) {
            freeGiftByQuantity = true
            break
          }
        }

        if (freeGiftByQuantity) {
          shouldAddFreeGift = true
          freeGiftQuantity = freeGiftQuantity + 1
        }
      }

      // If all items are gift cards, do not add free gift
      if (allGiftCards) {
        shouldAddFreeGift = false
      }

      const targetQuantity = shouldAddFreeGift ? Math.max(freeGiftQuantity, 0) : 0

      // Find existing free gift line item by variant id
      const existingFreeGift = cartItems.find(item => item.id === freeGiftVariantId)

      showGiftProduct.value = shouldAddFreeGift

      // No change needed
      // if (!existingFreeGift && targetQuantity === 0) return
      // if (existingFreeGift && existingFreeGift.quantity === targetQuantity) return

      // Remove free gift
      if (existingFreeGift && targetQuantity === 0) {
        await changeCart({
          key: existingFreeGift.key,
          quantity: 0
        })
        showGiftProduct.value = false
      }

      // Add new free gift
      if (!existingFreeGift && targetQuantity > 0) {
        await addCart({
          variantId: freeGiftVariantId,
          quantity: targetQuantity,
          properties: {
            _free_gift: true
          }
        })
      }

      // Update existing free gift quantity
      if (existingFreeGift && (targetQuantity > 0 && existingFreeGift.quantity !== targetQuantity)) {
        await changeCart({
          key: existingFreeGift.key,
          quantity: targetQuantity
        })
      }
    })

    return {}
  }
}
